import { customInstance } from "@repo/api-client";

/**
 * Client contract for the venue media manager (`/api/v1/venues/{venueId}/media`, media-uploads).
 * Authed backend calls funnel through the shared `customInstance` mutator (bearer token +
 * ProblemDetail handling, ADR-0020); the presigned PUT goes straight to storage with NO auth header.
 * Pure helpers (accept lists, the R6 alt-text gate, the quota meter) are exported separately so the
 * submit-blocking logic is unit-tested without a DOM or network.
 */

/** M1 exposes image purposes only (menu documents are a flagged backend follow-up). */
export type MediaPurpose = "COVER" | "LOGO" | "GALLERY" | "DISH";

export const MEDIA_PURPOSES: readonly MediaPurpose[] = ["COVER", "LOGO", "GALLERY", "DISH"];

export const ACCEPTED_IMAGE_TYPES: readonly string[] = ["image/jpeg", "image/png", "image/webp"];

/** True when the purpose keys a positioned slot (gallery/dish) rather than a singleton. */
export function isIndexedPurpose(purpose: MediaPurpose): boolean {
  return purpose === "GALLERY" || purpose === "DISH";
}

export interface MediaAssetView {
  id: string;
  purpose: string;
  position: number | null;
  state: string;
  rejectedStage: string | null;
  contentType: string;
  sizeBytes: number;
  altText: string | null;
  altDecorative: boolean;
  renditions: Record<string, string>;
  qualityFlag: boolean;
  supersededBy: string | null;
  createdAt: string;
}

export interface QuotaView {
  usedBytes: number;
  quotaBytes: number;
}

export interface CreateUploadResponse {
  assetId: string;
  key: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface CreateUploadInput {
  purpose: MediaPurpose;
  position?: number | null;
  contentType: string;
  sizeBytes: number;
  altText?: string | null;
  altDecorative?: boolean;
}

type Requester = <T>(url: string, init: RequestInit) => Promise<T>;

/**
 * The media client bound to a venue. `request` is injectable (defaults to the shared authed mutator)
 * so tests drive it without a real backend; `putBytes` is the raw storage PUT (no auth).
 */
export function createMediaClient(
  venueId: string,
  request: Requester = customInstance,
  putBytes: (url: string, file: Blob) => Promise<void> = defaultPutBytes,
) {
  const base = `/api/v1/venues/${venueId}/media`;
  return {
    createUpload: (input: CreateUploadInput): Promise<CreateUploadResponse> =>
      request<CreateUploadResponse>(base, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    uploadBytes: (uploadUrl: string, file: Blob): Promise<void> => putBytes(uploadUrl, file),
    confirm: (assetId: string): Promise<MediaAssetView> =>
      request<MediaAssetView>(`${base}/${assetId}/confirm`, { method: "POST" }),
    retire: (assetId: string): Promise<MediaAssetView> =>
      request<MediaAssetView>(`${base}/${assetId}/retire`, { method: "POST" }),
    list: (): Promise<MediaAssetView[]> => request<MediaAssetView[]>(base, { method: "GET" }),
    quota: (): Promise<QuotaView> => request<QuotaView>(`${base}/quota`, { method: "GET" }),
  };
}

export type MediaClient = ReturnType<typeof createMediaClient>;

async function defaultPutBytes(url: string, file: Blob): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) throw new Error(`upload failed with status ${res.status}`);
}

// --- pure helpers (unit-tested) ----------------------------------------------------------------

/** The `accept` attribute for the file input, scoped to the accepted image types. */
export function acceptAttr(): string {
  return ACCEPTED_IMAGE_TYPES.join(",");
}

export interface SubmitState {
  file: File | null;
  altText: string;
  decorative: boolean;
  uploading: boolean;
}

/**
 * R6: an image publish is impossible without alt text AND without a decorative marking. Submit is
 * blocked until a file is chosen, an accepted type, and one of {non-blank alt text, decorative}.
 */
export function isSubmitBlocked(state: SubmitState): boolean {
  if (state.uploading) return true;
  if (!state.file) return true;
  if (!ACCEPTED_IMAGE_TYPES.includes(state.file.type)) return true;
  return state.altText.trim().length === 0 && !state.decorative;
}

/** The quota meter fill, clamped to 0..100. */
export function quotaPercent(used: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((used / total) * 100)));
}

/** Human-readable byte size for the quota meter. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export type StatusTone = "pending" | "live" | "rejected" | "retired";

/** Maps a pipeline state to a status-chip tone. */
export function statusTone(state: string): StatusTone {
  switch (state) {
    case "LIVE":
      return "live";
    case "REJECTED":
      return "rejected";
    case "RETIRED":
      return "retired";
    default:
      return "pending";
  }
}

/** i18n key for a rejection stage, for the FR/EN reason text (R1 surfaced to the manager). */
export function rejectionReasonKey(stage: string | null): string {
  switch (stage) {
    case "SCREENING":
      return "media.rejected.screening";
    case "PROCESSING":
      return "media.rejected.processing";
    case "QUOTA":
      return "media.rejected.quota";
    default:
      return "media.rejected.validation";
  }
}
